import type { Query, QueryClient } from "@tanstack/react-query";
import {
  getLearningGetCourseDetailQueryKey,
  getLearningGetCourseDetailQueryOptions,
} from "@/services/api/learning/learning";
import type { CoursePlayerDetailDto } from "@/types/api";

// The generated key is a single URL string: split it around the course id to match every course's details.
const [detailUrlStart, detailUrlEnd] = getLearningGetCourseDetailQueryKey("\0")[0].split("\0");

const isCourseDetailQuery = (query: Query) => {
  const [url] = query.queryKey;
  return (
    query.queryKey.length === 1 &&
    typeof url === "string" &&
    url.startsWith(detailUrlStart) &&
    url.endsWith(detailUrlEnd) &&
    !url.slice(detailUrlStart.length).includes("/")
  );
};

/**
 * Marks every course's details stale once the learner completes a course: a paid course they have
 * through an in-order learning path unlocks when the course before it is complete (LMS-180), so a
 * course page cached as locked must be asked for again. It does not refetch: a course page refetches
 * when the learner opens it.
 */
export const invalidateCourseDetails = (queryClient: Pick<QueryClient, "invalidateQueries">) =>
  queryClient.invalidateQueries({ predicate: isCourseDetailQuery, refetchType: "none" });

/**
 * The course's details for the player. Cached details saying the learner can't open a paid course
 * may predate a learning path unlocking it (LMS-180), so they are asked for again before the player
 * turns the learner away.
 */
export const ensurePlayerCourseDetail = async (
  queryClient: Pick<QueryClient, "getQueryData" | "ensureQueryData" | "fetchQuery">,
  courseId: string,
): Promise<CoursePlayerDetailDto> => {
  const options = getLearningGetCourseDetailQueryOptions(courseId);
  const cached = queryClient.getQueryData<CoursePlayerDetailDto>(options.queryKey);
  if (cached?.isPaid && !cached.isEntitled) {
    return queryClient.fetchQuery({ ...options, staleTime: 0 });
  }
  return queryClient.ensureQueryData(options);
};
